import LocalizedStrings from 'react-localization';

let locStrings = new LocalizedStrings({
    de:
    {
        labelSortingCriteriaFilterAll:"-alle-",
        labelSortingCriteriaNone:"-keine-",
        labelSortingCriteriaAlphabetical:"Alphabetisch",
        labelSortingCriteriaNewestFirst:"Neueste zuerst",
        labelEmploymentTypeFullTime:"Vollzeit",
        labelEmploymentTypePartTime:"Teilzeit",
        labelEmploymentTypeContractor:"Auftragnehmer",
        labelEmploymentTypeTemporary:"befristete Stelle",
        labelEmploymentTypeIntern:"Praktikum",
        labelEmploymentTypeVolunteer:"Volontär",
        labelEmploymentTypePerDiem:"Tagelöhner",
        labelEmploymentTypeMiniJob:"Minijob",
        labelSalaryUnitHour:"Stundenlohn",
        labelSalaryUnitDay:"Tageslohn",
        labelSalaryUnitWeek:"Wochenlohn",
        labelSalaryUnitMonth:"Monatsgehalt",
        labelSalaryUnitYear:"Jahresgehalt",
        labelClientLanguageBrowserSetting:"Browser-Einstellung",
        labelClientLanguageGerman:"Deutsch",
        labelClientLanguageEnglish:"Englisch",
    },
    en: 
    {
        labelSortingCriteriaFilterAll:"-all-",
        labelSortingCriteriaNone:"-none-",
        labelSortingCriteriaAlphabetical:"Alphabetical",
        labelSortingCriteriaNewestFirst:"Newest first",
        labelEmploymentTypeFullTime:"Full time",
        labelEmploymentTypePartTime:"Part-time",
        labelEmploymentTypeContractor:"Contractor",
        labelEmploymentTypeTemporary:"Temporary position",
        labelEmploymentTypeIntern:"Internship",
        labelEmploymentTypeVolunteer:"Volunteer",
        labelEmploymentTypePerDiem:"Day laborer",
        labelEmploymentTypeMiniJob:"Mini job",
        labelSalaryUnitHour:"Hourly wage",
        labelSalaryUnitDay:"Daily wages",
        labelSalaryUnitWeek:"Weekly pay",
        labelSalaryUnitMonth:"Monthly salary",
        labelSalaryUnitYear:"Annual salary",
        labelClientLanguageBrowserSetting:"Browser setting",
        labelClientLanguageGerman:"German",
        labelClientLanguageEnglish:"English",
    }
});

function setLanguage(clientLanguage: ClientLanguage): void {
    switch(clientLanguage) { 
        case ClientLanguage.German: { 
           locStrings.setLanguage('de');
           break; 
        } 
        case ClientLanguage.English: { 
           locStrings.setLanguage('en'); 
           break; 
        } 
        // Otherwise "clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
    } 
}

export class JobService {
    private static baseUrl = process.env.REACT_APP_API_URL

    public static async getJobs(tenant: string, listName: string, forListView: boolean = false) {
        var url = `${JobService.baseUrl}/jobs/${tenant}/${listName}`;
        if (forListView) url += "?forListView=true"

        const data = await fetch(url)
            .then(response => {
                if (response.ok) {
                    return response.json()
                }
                else {
                    throw response.statusText
                }
            })
            .then(
                (data: JobList) => {
                    return data;
                }).catch(error => {
                    console.log(error)
                })

        return data;
    }

    public static async getJob(tenant, listName, id, previewMode = false) {
        const url = `${JobService.baseUrl}/jobs/${tenant}/${listName}/${id}?preview=${previewMode}`;
        const response = await fetch(url)
        if (response.ok) {
            const data = await (response).json()
            return data
        } else {
            throw response.status
        }
    }
}

export class ApplicationService {
    private static baseUrl = process.env.REACT_APP_API_URL

    public static async postApplication(data: FormData) {
        const url = `${ApplicationService.baseUrl}/applications`;
        const response = await fetch(url, {
            method: 'POST',
            body: data
        })
            .then(response => {
                if (response.ok) {
                    return response
                }
                else {
                    throw response.statusText
                }
            })
            .catch(error => {
                console.log(error)
            })

        return response
    }
}

export class SettingsService {
    private static baseUrl = process.env.REACT_APP_API_URL

    public static async getGeneralSettings(tenant) {
        const url = `${SettingsService.baseUrl}/settings/${tenant}/public`;
        const data = await (await fetch(url)).json();
        return data as GeneralSettings;
    }
}

export class StatisticService {
    private static baseUrl = process.env.REACT_APP_API_URL

    public static async updateCounter(tenant,jobId,counterName) {
        const url = `${StatisticService.baseUrl}/statistic`;
        const counter = { name: counterName,tenant:tenant,jobId:jobId } as Counter;
        const data = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(counter),
        })
            .then((data) => {
                console.log(data);
            })
            .catch((e) => {
                console.log(e);
            });
        return data;
    }
}

export interface Counter {
    name: string,
    tenant:string,
    jobId:string
}

export interface Colors {
    primary: string,
    onPrimary: string,
}

export interface JobList {
    id: string,
    name: string,
    jobs: Job[],
    legalNoticeUrl: string,
    dataSecurityUrl: string,
    fontFamily: string,
    formalAddressPronoun: boolean,
    subtitleOnJobPosting: boolean,
    showLocation: boolean,
    showEmploymentTypes: boolean,
    showExperienceLevels: boolean,
    showGroupings: boolean,
    showSorting: boolean,
    logoKey: string,
    headerKey: string,
    fontKey: string,
    fontAsBase64: string,
    colorsKey: string,
    colors: Colors,
    cssKey: string,
    customLink1:string,
    customLink2:string,
    customLink3:string,
    customLink4:string,
    customLink5:string,
    customForm:string;
    directIntegrationIframe:boolean;
    submitApplicationSuccessHeader:string;
    submitApplicationText:string;
    hideJobFilter:boolean;
    clientLanguage: ClientLanguage;
    supportUrl:string;
    supportEmail:string;
    supportPhoneNumber:string;
    supportCustomMessage:string;
    favIconKey:string;
}

export enum ClientLanguage {
    BrowserSetting, 
    German, 
    English 
}

export interface Job {
    id: string,
    title: string,
    subtitle: string,
    description: string,
    location: string,
    group: string,
    employmentTypes: Array<EmploymentType>,
    employmentTypesGoogle: Array<string>,
    experienceLevel: string,
    grouping: string,
    salaryMin: number,
    salaryMax: number,
    salaryCurrency: string,
    accessibilityLevel: "Public" | "Private" | "Deactivated",
    publishedAt?: Date,
    publishFrom: Date,
    publishUntil: Date,
    tenant: string,
    applicationCount: number,
    showApplicationCount: boolean,
    hiringOrganization: {
        name: string,
        sameAs: string,
    },
    postalAddress: {
        streetAddress: string,
        addressLocality: string,
        addressRegion: string,
        postalCode: string,
        addressCountry: string,
    },
    salaryUnit: SalaryUnit,
    customForm: string,
    cVoptional: boolean,
    logoKey: string,
    headerKey: string,
    fontKey: string,
    fontAsBase64: string,
    colorsKey: string,
    colors: Colors,
    locations: Array<string>,
    metaTitle: string,
    metaDescription: string,
    metaKeywords: string,
    metaURL: string,
    metaCanonical: string,
    metaRobotsTag: string,
    metaAuthor: string,
    metaPublisher: string,
    metaLang: string,
    experienceLevels: Array<string>,
    jobLists:Array<string>
}

export enum SortingCriteria {
    FilterAll,
    None,
    Alphabetical,
    NewestFirst,
}

export enum EmploymentType {
    None = "None",
    FullTime = "FullTime",  // Vollzeit
    PartTime = "PartTime",  // Teilzeit
    Contractor = "Contractor", // Auftragnehmer
    Temporary = "Temporary",  // befristete Stelle
    Intern = "Intern",     // Praktikant
    Volunteer = "Volunteer",  // Volontär
    PerDiem = "PerDiem",   // Tagelöhner
    MiniJob = "MiniJob",  // Minijob (Geringfuegige Beschaeftigung)
}

export enum SalaryUnit {
    Hour,
    Day,
    Week,
    Month,
    Year,
}

export interface JobForList {
    id: string,
    title: string,
    subtitle: string,
    location: string,
    group: string,
    employmentTypes: Array<EmploymentType>,
    experienceLevel: string,
    grouping: string,
    locations: Array<string>,
}

export interface Application {
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
    jobId: string,
    tenant: string,
    customProperties: CustomProperty[],
    jobList:string;
}

export interface CustomProperty {
    name: string,
    value: string
}

export interface GeneralSettings {
    textInputLimit: number,
    emailInputLimit: number,
    applicationPermittedExtensions: string[],
    applicationPermittedMimeTypes: string[],
    applicationFileNumberLimit: number,
    applicationFileSizeLimit: number,
    applicationPhoneNumberRegex: string,
    isUnicode: boolean,
    imagesUrl: string,
}

export function getSortingCriteria(type: SortingCriteria, clientLanguage: ClientLanguage | undefined): string {
    if (clientLanguage){
        setLanguage(clientLanguage);
    }
    if (type === undefined) return ""
    if (type === SortingCriteria.FilterAll) return locStrings.labelSortingCriteriaFilterAll
    if (type === SortingCriteria.None) return locStrings.labelSortingCriteriaNone
    if (type === SortingCriteria.Alphabetical) return locStrings.labelSortingCriteriaAlphabetical
    if (type === SortingCriteria.NewestFirst) return locStrings.labelSortingCriteriaNewestFirst
    return locStrings.labelSortingCriteriaNone
}

export function getEmploymentTypeAsString(type: EmploymentType, clientLanguage: ClientLanguage | undefined): string {
    if (clientLanguage){
        setLanguage(clientLanguage);
    }
    if (type === EmploymentType.None) return locStrings.labelSortingCriteriaFilterAll
    if (type === EmploymentType.FullTime) return locStrings.labelEmploymentTypeFullTime
    if (type === EmploymentType.PartTime) return locStrings.labelEmploymentTypePartTime
    if (type === EmploymentType.Contractor) return locStrings.labelEmploymentTypeContractor
    if (type === EmploymentType.Temporary) return locStrings.labelEmploymentTypeTemporary
    if (type === EmploymentType.Intern) return locStrings.labelEmploymentTypeIntern
    if (type === EmploymentType.Volunteer) return locStrings.labelEmploymentTypeVolunteer
    if (type === EmploymentType.PerDiem) return locStrings.labelEmploymentTypePerDiem
    if (type === EmploymentType.MiniJob) return locStrings.labelEmploymentTypeMiniJob
    return locStrings.labelEmploymentTypeFullTime
}

export function getSalaryUnitAsString(type: SalaryUnit, clientLanguage: ClientLanguage | undefined): string {
    if (clientLanguage){
        setLanguage(clientLanguage);
    }
    if (type === undefined) return ""
    if (type === SalaryUnit.Hour) return locStrings.labelSalaryUnitHour
    if (type === SalaryUnit.Day) return locStrings.labelSalaryUnitDay
    if (type === SalaryUnit.Week) return locStrings.labelSalaryUnitWeek
    if (type === SalaryUnit.Month) return locStrings.labelSalaryUnitMonth
    if (type === SalaryUnit.Year) return locStrings.labelSalaryUnitYear
    return locStrings.labelSalaryUnitMonth
}

export function getClientLanguageAsString(type: ClientLanguage): string {
    setLanguage(type);
    if (type === undefined) return ""
    if (type === ClientLanguage.BrowserSetting) return locStrings.labelClientLanguageBrowserSetting
    if (type === ClientLanguage.German) return locStrings.labelClientLanguageGerman
    if (type === ClientLanguage.English) return locStrings.labelClientLanguageEnglish
    return locStrings.labelClientLanguageGerman
}
